/**
 * S2, the waitlist variant. This is the real launch waitlist, not a stub.
 *
 * The marketing plan settled four things about this page, and all four are
 * decisions a reviewer should be able to see on the screen rather than read in
 * a document:
 *
 * 1. NO RESERVATION. The form never asks for a subdomain, because there is no
 *    reservation ledger behind it: names bind at provisioning and nothing can
 *    hold one for a person without an account. Advertising a held name would
 *    be exactly the overpromise a developer audience detects, and detection
 *    costs the brand more than the signup was worth. So the page says the true
 *    thing out loud instead of quietly omitting the field.
 *
 * 2. THE STAGE LADDER IS PUBLISHED, WITH A REASON PER STAGE. A closed door is
 *    not a problem as long as the person is told who is already inside, who is
 *    next, and why the order is that order. "We invite in small waves while we
 *    finish billing" is a real reason with a real end condition. "Soon" is not.
 *
 * 3. PDPA CONSENT IS A CHECKBOX, NOT A FOOTNOTE. This is a commercial product
 *    at a new address, so the school's privacy notice does not cover it. The
 *    consent states the single purpose and the withdrawal address in the same
 *    breath, and the button stays flat until it is ticked.
 *
 * 4. QUEUED ADMISSION. Submitting stores the entry in the control plane;
 *    candidates are invited in waves as capacity permits.
 *
 * States, every one deep linkable:
 *   /waitlist                     resting, the form
 *   /waitlist?state=joined        confirmed on the waitlist.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Checkbox } from '../../components/primitives/Checkbox';
import { Field, FieldFrame, describedBy } from '../../components/primitives/Field';
import { HeroBackdrop } from '../../components/patterns/HeroBackdrop';
import { Notice } from '../../components/patterns/Notice';
import { Wordmark } from '../../components/brand/Wordmark';

import { apiFetch, ApiError } from '../../lib/http';
import { t } from '../../lib/i18n/t';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import { useFormTracker } from '../../lib/analytics/use-form-tracker';

import '../../styles/screens/auth.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';
const STATES = ['resting', 'joined'] as const;
type State = (typeof STATES)[number];

/**
 * What was submitted, kept so a reload of ?state=joined still knows whose
 * address it is confirming.
 *
 * The payoff screen echoes the address back, which is the one fact on it
 * worth anything, and it was reading that off component state, gone the
 * moment the tab reloaded. What a person then saw was the demo fixture's
 * address, which is worse than showing nothing: a confirmation screen
 * quietly naming somebody else's email is a reason to distrust the whole
 * product. localStorage rather than sessionStorage on purpose, so the
 * confirmation survives closing the tab and coming back to the link.
 */
const JOINED_KEY = 'sv-cloud-waitlist-joined';

/** The typed-but-unsent form, so a reload mid-form does not start over. */
const DRAFT_KEY = 'sv-cloud-waitlist-draft';

interface JoinedRecord {
  email: string;
  building: string;
}

function readStored(key: string): JoinedRecord | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<JoinedRecord>;
    if (typeof parsed.email !== 'string') return null;
    return { email: parsed.email, building: typeof parsed.building === 'string' ? parsed.building : '' };
  } catch {
    // A private window, cleared site data, or a browser refusing storage.
    // The screen still works: it just cannot echo the address back.
    return null;
  }
}

function writeStored(key: string, record: JoinedRecord): void {
  try {
    localStorage.setItem(key, JSON.stringify(record));
  } catch {
    /* Storage is a convenience here, never a requirement. */
  }
}

/* ---------------------------------------------------------- stage ladder */

interface Stage {
  term: string;
  reason: ReactNode;
  now: boolean;
}

function StageLadder() {
  const stages: Stage[] = [
    {
      term: t('Waitlist, now'),
      reason: t('We invite in small waves while we finish billing.'),
      now: true,
    },
    {
      term: t('SV Academy Graduates'),
      reason: (
        <>
          {t('Invited automatically.')}{' '}
          <a
            className="link-inline"
            href="https://sv-academy.org"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('Join SV Academy')}
          </a>
        </>
      ),
      now: false,
    },
    {
      term: t('Everyone'),
      reason: t('When the doors open.'),
      now: false,
    },
  ];

  return (
    <section className="wl__ladder" aria-label={t('Who gets in, and when')}>
      <h2 className="wl__ladder-title">{t('Who gets in, and when')}</h2>
      <dl className="wl__stages">
        {stages.map((stage) => (
          <div className="wl__stage" key={stage.term} data-now={stage.now ? 'yes' : 'no'}>
            <span className="wl__stage-mark" aria-hidden="true" />
            <dt className="wl__stage-term">{stage.term}</dt>
            <dd className="wl__stage-reason">{stage.reason}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/* ------------------------------------------------------------ the screen */

export default function Waitlist() {
  const account = useDemoStore((s) => s.account);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = searchParams.get('state');
  const [state, setState] = useState<State>(urlState === 'joined' ? 'joined' : 'resting');

  const joined = readStored(JOINED_KEY);
  const draft = readStored(DRAFT_KEY);
  const [email, setEmail] = useState(() => joined?.email ?? draft?.email ?? '');
  const [building, setBuilding] = useState(() => joined?.building ?? draft?.building ?? '');
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formTracker = useFormTracker({
    formId: 'waitlist',
    formName: 'Join Waitlist',
    totalFields: 3,
  });

  // Worth watching precisely because it used to be broken: how often the
  // confirmation screen is reached by a reload or a revisit rather than by
  // the submit that created it. Every one of those used to show the demo
  // fixture's address.
  const echoSeenRef = useRef(false);
  const submittedHereRef = useRef(false);
  useEffect(() => {
    if (state !== 'joined' || echoSeenRef.current) return;
    echoSeenRef.current = true;
    // Not derived from `email`: that is seeded from storage on mount, so it
    // is non-empty on a reload too and would report every visit as fresh.
    captureEvent('waitlist_confirmation_viewed', {
      restored_from_storage: !submittedHereRef.current,
    });
  }, [state]);

  // Sync state if URL search param changes
  useEffect(() => {
    if (urlState === 'joined') {
      setState('joined');
    } else if (!urlState) {
      setState('resting');
    }
  }, [urlState]);

  function transitionTo(next: State) {
    setState(next);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'resting') p.delete('state');
        else p.set('state', next);
        return p;
      },
      { replace: true },
    );
  }

  // A reload of ?state=joined has nothing in component state, so the echo
  // comes from what was actually submitted, remembered above. The demo
  // account is the last resort and only reachable in mock mode, where the
  // deep link is a design-review artefact rather than a real confirmation.
  const echoed = email.trim() || joined?.email || account.email;

  // Flat until both halves are true.
  const ready = email.trim().length > 0 && consented && !submitting;

  const buildingId = 'wl-building';
  const buildingHelp = t('One sentence is enough. We read them, and they shape who we invite first.');

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!ready) return;
    setSubmitting(true);
    setError(null);
    formTracker.trackSubmit({ has_building_description: building.trim().length > 0 });
    try {
      if (!USE_MOCK) {
        await apiFetch('/api/v1/waitlist', {
          method: 'POST',
          body: {
            email: email.trim(),
            building: building.trim() || undefined,
          },
          unauthenticated: true,
        });
      } else {
        // In mock mode, brief simulation delay
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      formTracker.trackSubmitSuccess();
      captureEvent('waitlist_joined');
      submittedHereRef.current = true;
      writeStored(JOINED_KEY, { email: email.trim(), building: building.trim() });
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {
        /* See writeStored. */
      }
      transitionTo('joined');
    } catch (err) {
      const errorMsg = err instanceof ApiError ? err.message : t('Unable to join waitlist. Please try again.');
      const errorCode = err instanceof ApiError ? err.code : 'unknown';
      formTracker.trackSubmitError(errorCode, errorMsg);
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  }

  if (state === 'joined') {
    return (
      <div className="auth">
        <HeroBackdrop reach="tight" />

        <div className="auth__lockup">
          <Wordmark size={32} />
        </div>

        <div className="auth__card">
          <h1 className="auth__title">{t("You're on the list.")}</h1>

          <Notice tone="done">
            <p className="mono">{echoed}</p>
          </Notice>

          <p className="auth__say">
            {t("We'll email you at {email} when it's your turn. See you soon.", { email: echoed })}
          </p>

          {/* The ladder stays on the payoff screen, because "when" is still
              the only question this person has. */}
          <StageLadder />
        </div>
      </div>
    );
  }

  return (
    <div className="auth">
      <HeroBackdrop reach="tight" />

      <div className="auth__lockup">
        <Wordmark size={32} />
      </div>

      <div className="auth__card">
        <h1 className="auth__title">{t('Join the SV Cloud waitlist')}</h1>
        <p className="auth__say">
          {t('SV Cloud is not open to everybody yet. This is the order we are opening it in.')}
        </p>

        <StageLadder />

        {error ? (
          <Notice tone="attention">
            <p>{error}</p>
          </Notice>
        ) : null}

        <form className="auth__form" onSubmit={handleSubmit}>
          <Field
            label={t('Email address')}
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onFocus={() => formTracker.onFieldFocus('email')}
            onBlur={() => formTracker.onFieldBlur('email')}
            onChange={(event) => {
              setEmail(event.target.value);
              writeStored(DRAFT_KEY, { email: event.target.value, building });
              formTracker.onFieldChange('email', event.target.value);
            }}
          />

          {/* The one optional sentence. It is a textarea rather than an input
              because a person who has something to say about their project
              should be able to see all of it, and it wears the field frame so
              the label, the optional marker and the help line sit exactly
              where they sit on every other form in the product. */}
          <FieldFrame
            label={t('What do you want to build?')}
            controlId={buildingId}
            help={buildingHelp}
            aside={t('Optional')}
          >
            <textarea
              id={buildingId}
              className="field__input wl__area"
              name="building"
              rows={2}
              value={building}
              onFocus={() => formTracker.onFieldFocus('building')}
              onBlur={() => formTracker.onFieldBlur('building')}
              onChange={(event) => {
                setBuilding(event.target.value);
                writeStored(DRAFT_KEY, { email, building: event.target.value });
                formTracker.onFieldChange('building', event.target.value);
              }}
              placeholder={t('A booking page for my restaurant.')}
              aria-describedby={describedBy(buildingId, buildingHelp)}
            />
          </FieldFrame>

          <Checkbox
            label={t('We keep your email to invite you and for nothing else.')}
            help={t('Withdraw any time at hello@sv-academy.org.')}
            name="consent"
            required
            checked={consented}
            onChange={(event) => {
              setConsented(event.target.checked);
              formTracker.onFieldChange('consent', event.target.checked);
            }}
          />

          <div className="auth__wide">
            <Button variant="solid" type="submit" disabled={!ready}>
              {submitting ? t('Joining...') : t('Join the waitlist')}
            </Button>
          </div>
        </form>

        {/* The no reservation stance, said rather than omitted. */}
        <p className="wl__note">
          {t(
            'We are not holding web addresses. You pick yours when you make your first project, and you can change it later.',
          )}
        </p>

        <p className="auth__foot">
          {t('Already have an account?')}{' '}
          <Link className="link-inline" to="/sign-in">
            {t('Sign in')}
          </Link>
        </p>
      </div>
    </div>
  );
}
